import React, { useCallback, useMemo } from 'react';
import * as Routes from '@/modules/routes';
import cn from '@/utils/cn';
import SPAAccountMenu from '../SysadminSPA/shared/SPAAccountMenu';
import SPABrandLink from '../SysadminSPA/shared/SPABrandLink';
import { useSPANav } from '../SysadminSPA/shared/SPANav';
import SPARail from '../SysadminSPA/shared/SPARail';
import useCollapsedRail from './railCollapse';

interface LegacyDrillRailProps {
  // Where the crumb goes when there is no page to return to. The page's SPANavSlot names an SPA
  // route; on a legacy page the way back is a legacy page, so the host chooses it.
  backHref: string;
  signOutPath: string;
  userName: string;
}

// The page this one was opened from, when it is another page on this site — a fresh tab, a
// reload, or an outside link has none, and the crumb falls back to the host's choice.
const referrerHref = () => {
  try {
    const referrer = new URL(document.referrer);
    const here = window.location;

    if (referrer.origin === here.origin && referrer.pathname !== here.pathname) return referrer.href;
  } catch {
    // No or unparsable referrer.
  }

  return null;
};

// AIDEV-NOTE: The SPA rail drilled into one page, for a legacy mount: the page's SPANavSlot
// portals its pane in here and sets the crumb (back label · title), the way SPANav hosts it in
// the SPA. No app tree at rest — the crumb is the way out: a full-load link to the page you came
// from, or to the host's fallback.
const LegacyDrillRail = ({ backHref, signOutPath, userName }: LegacyDrillRailProps) => {
  const { pageHeader, setSlotEl } = useSPANav();
  const [collapsed, setCollapsed] = useCollapsedRail();
  const slotRef = useCallback((el: HTMLElement | null) => setSlotEl(el), [setSlotEl]);
  const href = useMemo(() => referrerHref() ?? backHref, [backHref]);

  const crumb = pageHeader && (
    <a
      className={cn(
        '-tw-mx-1.5 tw-flex tw-min-w-0 tw-items-center tw-gap-1.5 tw-rounded-md tw-px-1.5 tw-py-1',
        'tw-text-sm tw-font-bold tw-text-spa-heading tw-no-underline',
        'hover:tw-bg-spa-bg-tertiary hover:tw-no-underline hover:tw-text-spa-heading',
      )}
      href={href}
    >
      <span aria-hidden="true" className="tw-shrink-0 tw-font-normal tw-text-spa-accent-text">‹</span>
      <span className="tw-shrink-0 tw-whitespace-nowrap tw-font-normal tw-text-spa-tertiary">
        {`${pageHeader.backLabel} ·`}
      </span>
      <span className="tw-min-w-0 tw-truncate">{pageHeader.title}</span>
    </a>
  );

  return (
    <SPARail
      collapsed={collapsed}
      collapseLabel="Collapse content rail"
      expandLabel="Expand content rail"
      footer={<SPAAccountMenu className="tw-w-full" signOutPath={signOutPath} userName={userName} />}
      header={<SPABrandLink href={Routes.admin_homes_path()} />}
      headerClassName={cn('tw-items-center tw-pb-3', 'tw-border-0 tw-border-b tw-border-solid tw-border-spa-border')}
      subheader={crumb && <div className="tw-flex tw-min-w-0 tw-items-center">{crumb}</div>}
      onCollapsedChange={setCollapsed}
    >
      <div ref={slotRef} className="tw-flex tw-flex-col" />
    </SPARail>
  );
};

export default LegacyDrillRail;
